/**
 * normalize — one wire envelope → one [NormalizedEvent](../model/types.ts).
 *
 * The whole job is to flatten `event_type` into `(domain, phase)` and lift the
 * correlation ids out of the opaque `data` bag, so that everything downstream
 * works in two dimensions instead of sixty event names.
 */

import type { NormalizedEvent } from 'src/content/ChatV2/steps/model/types';
import type { WireEnvelope } from 'src/content/ChatV2/transport/eventSchema';

import { parseEventType } from './eventType';

function asId(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value === '' ? undefined : value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return undefined;
}

export interface NormalizeOptions {
  ordinal: number;
  receivedAt?: number;
}

export function normalizeEvent(
  envelope: WireEnvelope,
  options: NormalizeOptions
): NormalizedEvent {
  const parsed = parseEventType(envelope.event_type ?? envelope.event);
  const data: Record<string, unknown> =
    envelope.data && typeof envelope.data === 'object' ? envelope.data : {};

  return {
    raw: envelope,
    domain: parsed.domain,
    channel: parsed.channel,
    mode: parsed.mode,
    phase: parsed.phase,
    phaseRaw: parsed.phaseRaw,

    turnId: asId(envelope.rid),
    chatId: asId(envelope.chat_id),
    runId: asId(data.run_id),
    parentRunId: asId(data.parent_run_id),

    data,
    ordinal: options.ordinal,
    receivedAt: options.receivedAt ?? Date.now()
  };
}
